import { createAuthClient } from 'better-auth/react';
import { criarClienteApi, ErroApiNyroh, type ClienteApi } from '@nyroh/contracts';

import { exigirEnv } from './env';

/**
 * ============================================================================
 * O CANAL UNICO DO PAINEL COM O SERVIDOR
 * ============================================================================
 *
 * Este arquivo substitui `lib/supabase.ts` e o `createClient` que vivia dentro de
 * `services/db.ts`.
 *
 * A diferenca nao e de biblioteca, e de arquitetura: antes o NAVEGADOR era o
 * servidor de aplicacao e falava direto com o Postgres, carregando a chave do
 * banco dentro do bundle. Com doze policies `USING (true)` do outro lado, isso
 * significava que qualquer visitante podia ler e apagar tudo.
 *
 * Agora o navegador so conhece a URL da API. Nao existe credencial de banco no
 * bundle porque nao existe conexao com o banco a partir daqui.
 */

export const API_URL = exigirEnv(
  'VITE_API_URL',
  import.meta.env.VITE_API_URL,
  'URL da API Nyroh (ex.: http://localhost:3333 no ambiente local)',
);

/** Base publica do CDN de imagens. E dela que sai a URL de cada `chaveR2`. */
export const CDN_ASSETS = exigirEnv(
  'VITE_CDN_BASE_ASSETS',
  import.meta.env.VITE_CDN_BASE_ASSETS,
  'base publica do bucket de imagens (R2 em producao, MinIO no local)',
);

/*
 * VITE_CDN_BASE_SITES nao existe mais aqui, de proposito.
 *
 * O painel lia o HTML publicado direto do CDN, montando essa base + a chave do
 * R2. Isso exigia que o bucket de sites fosse publico E que a variavel
 * apontasse para o dominio ligado a ELE — e um dominio de R2 serve um bucket
 * so. Com esta variavel e a de imagens no mesmo endereco, a leitura caia no
 * bucket errado e voltava `404 Object not found` com o objeto intacto no bucket
 * certo; nada na mensagem apontava para a causa.
 *
 * Agora quem le o artefato e a API (`sites.obterHtmlVersao`), que ja tem
 * credencial do R2. Uma variavel a menos para errar, e o bucket de sites nao
 * precisa mais ser publico.
 *
 * Quem serve os visitantes continua sendo o Worker da borda, lendo o R2 por
 * binding — nada disso passa pela API.
 */

/**
 * Dominio base dos sites publicados. O endereco de um site e
 * `<slug>.<este valor>`, servido pelo Worker da borda a partir do R2.
 *
 * Existe porque o painel mostrava o endereco publico como
 * `window.location.origin + '#/site/' + slug` — um link no dominio do PAINEL,
 * que passa por uma rota `acesso: 'operador'` e devolve 401 para qualquer
 * visitante. Era um "link publico" que so funcionava para quem ja estava
 * logado, e nao era o endereco onde o site realmente responde.
 *
 * O espelho no servidor e `DOMINIO_BASE_SITES` (packages/api/src/comum/env.ts).
 * Os dois precisam ter o mesmo valor: e ele que a API usa para montar o
 * `urlPublica` de cada site e para invalidar o KV da borda na publicacao.
 */
export const DOMINIO_BASE_SITES = exigirEnv(
  'VITE_DOMINIO_BASE_SITES',
  import.meta.env.VITE_DOMINIO_BASE_SITES,
  'dominio base dos sites publicados, ex.: nyroh.com.br',
);

/**
 * Cliente de autenticacao do operador.
 *
 * Substitui as tres senhas de administrador comparadas como string literal dentro
 * do bundle e a sessao em `localStorage.setItem('ditho_admin_auth','true')`, que
 * qualquer pessoa forjava pelo console em uma linha.
 *
 * A sessao agora e um cookie httpOnly assinado pelo servidor: o JavaScript da
 * pagina nao consegue le-lo nem inventa-lo.
 */
export const auth = createAuthClient({
  baseURL: `${API_URL}/api/auth`,
  fetchOptions: { credentials: 'include' },
});

/**
 * Sessao do PORTAL DO CLIENTE (usuario + PIN).
 *
 * Este token e opaco e de vida curta, e fica em `sessionStorage` — nao em
 * `localStorage` — porque deve morrer com a aba. Ele nao concede acesso a dado
 * de outro cliente: o servidor resolve o cliente a partir do proprio token.
 */
const CHAVE_TOKEN_PORTAL = 'nyroh:portal';

export function tokenDoPortal(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_TOKEN_PORTAL);
  } catch {
    // Navegador com armazenamento bloqueado. Sem sessao persistida, o portal
    // pede login de novo — que e degradacao aceitavel, nao erro.
    return null;
  }
}

export function guardarTokenDoPortal(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(CHAVE_TOKEN_PORTAL, token);
    else sessionStorage.removeItem(CHAVE_TOKEN_PORTAL);
  } catch {
    // idem
  }
}

/**
 * Cliente HTTP tipado, gerado do contrato em `@nyroh/contracts`.
 *
 * Rota inexistente, parametro faltando e corpo com formato errado nao compilam.
 * E a correcao estrutural do BUG-2: quatro rotas que o front chamava e o servidor
 * nao expunha, falhando em silencio dentro de `catch {}` vazio por meses.
 */
export const api: ClienteApi = criarClienteApi({
  baseUrl: API_URL,
  obterToken: () => tokenDoPortal() ?? undefined,
  comCredenciais: true,
  aoErro: (erro) => {
    if (erro.precisaAutenticar) {
      guardarTokenDoPortal(null);
    }
    // Erro nunca e engolido: ou a tela trata, ou aparece aqui. O repositorio tem
    // 28 `catch {}` vazios, e foi por eles que a area do vendedor "funcionou"
    // meses sem gravar nada.
    console.error(`[api] ${erro.rota ?? '?'} -> ${erro.codigo}: ${erro.message}`);
  },
});

export { ErroApiNyroh };

/** URL publica de um objeto de imagem a partir da chave guardada no banco. */
export function urlDaImagem(chaveR2: string): string {
  return `${CDN_ASSETS.replace(/\/+$/, '')}/${chaveR2.replace(/^\/+/, '')}`;
}

/**
 * Sobe uma imagem DIRETO para o R2, com URL pre-assinada.
 *
 * Os bytes nao passam pela API: ela so autoriza. E o que evita 500 KB por imagem
 * atravessando o processo Node — e o que impede a volta do base64, que foi como
 * o banco antigo estourou cota.
 */
export async function enviarImagem(
  arquivo: File,
  destino: { siteId?: string; clienteId?: string } = {},
): Promise<{ chaveR2: string; url: string }> {
  const assinatura = await api.chamar('assets.assinarUpload', {
    corpo: {
      nomeArquivo: arquivo.name,
      mime: arquivo.type,
      bytes: arquivo.size,
      ...destino,
    },
  });

  const resposta = await fetch(assinatura.url, {
    method: 'PUT',
    headers: { 'content-type': arquivo.type },
    body: arquivo,
  });
  if (!resposta.ok) {
    throw new Error(
      `Falha ao enviar a imagem para o armazenamento (${resposta.status}). ` +
        'Se estiver no ambiente local, confira se o MinIO esta no ar.',
    );
  }

  const registrado = await api.chamar('assets.registrar', {
    corpo: {
      chaveR2: assinatura.chaveR2,
      mime: arquivo.type,
      bytes: arquivo.size,
      ...destino,
    },
  });

  return { chaveR2: assinatura.chaveR2, url: registrado.url };
}
